import { GetSensorResponse } from 'api/actions/get-sensor/get-sensor-response';
import { UpdateSensorAlertSettingsPayload } from 'api/actions/update-sensor-alert-settings/update-sensor-alert-settings-payload';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import SensorAlertsPresenter from 'components/SensorAlerts/SensorAlertsPresenter';
import { useNavigate } from 'react-router-dom';
import { SENSOR_DETAIL_EDIT_PAGE_PATH } from 'routes/paths';

type SensorAlert = NonNullable<GetSensorResponse['sensor']['alerts']>[number];

interface SensorAlertsProps {
  sensor: {
    id?: number;
    name?: string;
    alerts?: SensorAlert[];
  };
  _t: (msgid: string) => string;
  onAlertToggle?: (alertTitle: string, enabled: boolean) => void;
}

/**
 * A component that displays a list of sensor alerts and their statuses.
 * @prop {object} sensor - The sensor object containing the alerts.
 * @prop {function} _t - A translation function.
 * @returns {JSX.Element} A component that displays the sensor alerts.
 */
export default function SensorAlerts({ sensor, _t, onAlertToggle }: SensorAlertsProps) {
  const api = useApi();
  const navigate = useNavigate();
  const [localAlerts, setLocalAlerts] = useState(sensor.alerts ?? []);

  useEffect(() => {
    setLocalAlerts(sensor.alerts ?? []);
  }, [sensor.alerts]);

  /**
   * Handles the toggle of an alert.
   * @param alertType The type of the alert.
   * @param currentStatus The current status of the alert.
   */
  const handleToggle = async (alertType: SensorAlert['type'], currentStatus: boolean | undefined) => {
    if (!sensor?.id || !alertType) return;

    const newStatus = !(currentStatus ?? false);

    try {
      const alert = localAlerts.find((item) => item.type === alertType);
      if (!alert) return;

      const limitField = alert.fields?.find((field) => field.name === 'limit' && field.type === 'number');
      const requiresLimit = alertType === 'lower-limit' || alertType === 'upper-limit';

      if (requiresLimit && newStatus && (limitField?.value === undefined || limitField.value === null)) {
        navigate(SENSOR_DETAIL_EDIT_PAGE_PATH.insert({ sensorId: sensor.id }));
        return;
      }

      setLocalAlerts((prev) =>
        prev.map((localAlert) => (localAlert.type === alertType ? { ...localAlert, active: newStatus } : localAlert))
      );

      const setting =
        alertType === '24h-consumption'
          ? { type: alertType, enabled: newStatus, limit: limitField?.value ?? 1000 }
          : limitField?.type === 'number'
            ? { type: alertType, enabled: newStatus, limit: limitField.value }
            : { type: alertType, enabled: newStatus };

      const payload: UpdateSensorAlertSettingsPayload = { alertSettings: [setting] };

      await api.getAction('UpdateSensorAlertSettings')({
        parameters: { sensorId: sensor.id },
        payload,
      });

      onAlertToggle?.(alert.title ?? '', newStatus);
      notifications.show({
        title: _t('Information'),
        message: _t('Alert settings updated.'),
      });
    } catch (error) {
      panic(error as Error);
    }
  };

  if (!localAlerts || localAlerts.length === 0) {
    return null;
  }

  return <SensorAlertsPresenter sensorId={sensor.id} alerts={localAlerts} _t={_t} onToggle={handleToggle} />;
}
